import React, { useEffect, useState } from 'react';

import { Link, useNavigate, useParams } from 'react-router-dom';

import Button from '../../../components/common/Button';
import Card from '../../../components/common/Card';
import CourseRequestStatusBadge from '../../../components/common/CourseRequestStatusBadge';
import FormField from '../../../components/common/FormField';
import AdminLayout from '../../../components/layout/AdminLayout';
import { courseRequestStatusName } from '../../../constants/CourseRequestStatusConstants';
import { useCourseSearch } from '../../../hooks/useCourseApi';
import {
  useApproveCourseRequest,
  useCourseRequestById,
  useDeclineCourseRequest,
} from '../../../hooks/useCourseRequestApi';
import { CourseDTO } from '../../../services/apiClient';
import { convertToUserTime } from '../../../utils/dateUtils/convertToUserTime';
import { showToast } from '../../../utils/showToast';
import { confirmAction } from '../../../utils/sweetAlertUtils';

// Words nearly every course name has, which match thousands of courses and so say nothing about
// whether this one is already here.
const GENERIC_WORDS = new Set([
  'the',
  'and',
  'golf',
  'club',
  'country',
  'estate',
  'course',
  'links',
  'resort',
  'gc',
  'cc',
]);

// The duplicate check searches on the most telling word of the requested name rather than the
// whole thing, because the search is a plain "contains". "Fancourt Links Golf Club" would miss a
// course filed as "Fancourt (The Links)"; "Fancourt" finds it.
const suggestedSearchTerm = (courseName: string): string => {
  const telling = courseName
    .split(/[\s,()&/-]+/)
    .find((word) => word.length > 2 && !GENERIC_WORDS.has(word.toLowerCase()));
  return telling ?? courseName.trim();
};

// A member typed this, so it may well have no scheme. Without one the browser treats it as a
// path on our own site.
const asExternalUrl = (url: string): string =>
  /^https?:\/\//i.test(url) ? url : `https://${url}`;

const messageFrom = (err: unknown, fallback: string): string => {
  const failure = err as {
    message?: unknown;
    result?: { message?: unknown };
  } | null;
  const apiMessage = failure?.result?.message ?? failure?.message;
  return typeof apiMessage === 'string' && apiMessage.trim().length > 0
    ? apiMessage
    : fallback;
};

const DetailRow: React.FC<{ label: string; children: React.ReactNode }> = ({
  label,
  children,
}) => (
  <div className="grid grid-cols-1 gap-1 py-2 sm:grid-cols-3 sm:gap-4">
    <dt className="text-sm font-medium text-gray-500">{label}</dt>
    <dd className="text-sm text-ink sm:col-span-2">{children}</dd>
  </div>
);

const CourseRequestReview: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { data: request, isLoading } = useCourseRequestById(id);
  const approve = useApproveCourseRequest();
  const decline = useDeclineCourseRequest();
  const { search } = useCourseSearch();
  const navigate = useNavigate();

  const [reviewNotes, setReviewNotes] = useState('');
  const [matchTerm, setMatchTerm] = useState('');
  const [matches, setMatches] = useState<CourseDTO[]>([]);
  const [isSearching, setIsSearching] = useState(false);

  const breadcrumbs = [
    { label: 'Admin', href: '#' },
    { label: 'Course Requests', href: '/admin/course-requests' },
    { label: 'Review', href: '#' },
  ];

  const isPending = courseRequestStatusName(request?.status) === 'Pending';

  const runMatchSearch = async (term: string): Promise<void> => {
    if (term.trim().length === 0) {
      setMatches([]);
      return;
    }
    setIsSearching(true);
    try {
      const response = await search({
        page: 1,
        pageSize: 10,
        sortBy: 'Name',
        sortDescending: false,
        filters: { name: term.trim() },
      });
      setMatches(response.results);
    } catch (error) {
      console.error('Error searching for matching courses:', error);
    } finally {
      setIsSearching(false);
    }
  };

  // Look for a duplicate as soon as the request loads, so an admin sees "this is already here"
  // before they reach for Create.
  useEffect(() => {
    if (!request?.courseName || !isPending) {
      return;
    }
    const term = suggestedSearchTerm(request.courseName);
    setMatchTerm(term);
    runMatchSearch(term);
    // Keyed on the request id alone: this is the opening suggestion, run once per request. After
    // that the admin drives the search box, and re-running on every render would overwrite it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [request?.id]);

  const handleLink = async (course: CourseDTO): Promise<void> => {
    const confirmed = await confirmAction({
      title: 'Link to this course?',
      text: `The request will be approved as "${course.name}", and the member will be emailed that it is on GolfPlayed.`,
      confirmButtonText: 'Yes, approve',
      icon: 'question',
    });
    if (!confirmed || !id || !course.id) {
      return;
    }

    try {
      await approve.mutateAsync({
        id,
        courseId: course.id,
        reviewNotes: reviewNotes.trim() || undefined,
      });
      showToast('Request approved', 'success');
      navigate('/admin/course-requests');
    } catch (err) {
      showToast(messageFrom(err, 'Could not approve the request.'), 'error');
    }
  };

  const handleDecline = async (): Promise<void> => {
    const confirmed = await confirmAction({
      title: 'Decline this request?',
      text: 'The member will not be emailed.',
      confirmButtonText: 'Yes, decline',
    });
    if (!confirmed || !id) {
      return;
    }

    try {
      await decline.mutateAsync({
        id,
        reviewNotes: reviewNotes.trim() || undefined,
      });
      showToast('Request declined', 'success');
      navigate('/admin/course-requests');
    } catch (err) {
      showToast(messageFrom(err, 'Could not decline the request.'), 'error');
    }
  };

  const isBusy = approve.isPending || decline.isPending;

  return (
    <AdminLayout
      activeMenuItem="/admin/course-requests"
      breadcrumbs={breadcrumbs}
      allowedRoles={['Admin']}
      requiredPermission="Settings.Course.Edit"
    >
      {isLoading || !request ? (
        <p>{isLoading ? 'Loading...' : 'That course request was not found.'}</p>
      ) : (
        <div className="space-y-6">
          <Card
            title={request.courseName}
            headerActions={<CourseRequestStatusBadge status={request.status} />}
          >
            <dl className="divide-y divide-gray-100">
              <DetailRow label="Country">
                {request.countryName || '-'}
              </DetailRow>
              <DetailRow label="State or province">
                {request.stateName || '-'}
              </DetailRow>
              {/* Only on requests sent before the form asked for a state instead. */}
              {request.location && (
                <DetailRow label="Town or area">{request.location}</DetailRow>
              )}
              <DetailRow label="Website">
                {request.websiteUrl ? (
                  <a
                    href={asExternalUrl(request.websiteUrl)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-primary-deep underline"
                  >
                    {request.websiteUrl}
                  </a>
                ) : (
                  '-'
                )}
              </DetailRow>
              <DetailRow label="Notes">
                <span className="whitespace-pre-line">
                  {request.notes || '-'}
                </span>
              </DetailRow>
              <DetailRow label="Requested by">
                {request.memberName}
                {request.memberEmail && (
                  <span className="text-gray-500">
                    {' '}
                    ({request.memberEmail})
                  </span>
                )}
              </DetailRow>
              <DetailRow label="Requested">
                {convertToUserTime(request.createdUtc?.toString() ?? '')}
              </DetailRow>

              {!isPending && (
                <>
                  <DetailRow label="Reviewed by">
                    {request.reviewedByName || '-'}
                    {request.reviewedUtc && (
                      <span className="text-gray-500">
                        {' '}
                        on {convertToUserTime(request.reviewedUtc.toString())}
                      </span>
                    )}
                  </DetailRow>
                  {request.courseId && (
                    <DetailRow label="Course">
                      <Link
                        to={`/admin/courses/${request.courseId}`}
                        className="text-primary-deep underline"
                      >
                        {request.resolvedCourseName || 'View course'}
                      </Link>
                    </DetailRow>
                  )}
                  <DetailRow label="Review note">
                    <span className="whitespace-pre-line">
                      {request.reviewNotes || '-'}
                    </span>
                  </DetailRow>
                </>
              )}
            </dl>
          </Card>

          {isPending && (
            <Card
              title="Review"
              subtitle="Add the course, link it to one that is already here, or decline it."
            >
              <div className="space-y-6">
                <FormField
                  id="reviewNotes"
                  label="Review note (optional, admins only)"
                  type="textarea"
                  rows={2}
                  maxLength={1000}
                  value={reviewNotes}
                  onChange={(e) => setReviewNotes(e.target.value)}
                  helper="Saved with a link or a decline. Not sent to the member."
                />

                <div>
                  <h3 className="mb-2 text-sm font-semibold text-ink">
                    Not on GolfPlayed yet
                  </h3>
                  <Button
                    onClick={() =>
                      navigate(
                        `/admin/courses/create?fromRequest=${request.id}`,
                      )
                    }
                    disabled={isBusy}
                  >
                    Create course from this request
                  </Button>
                  <p className="mt-1 text-xs text-gray-500">
                    Opens the course form with the name, country and website
                    filled in. Saving the course approves this request.
                  </p>
                </div>

                <div>
                  <h3 className="mb-2 text-sm font-semibold text-ink">
                    Already on GolfPlayed?
                  </h3>
                  <form
                    onSubmit={(e) => {
                      e.preventDefault();
                      runMatchSearch(matchTerm);
                    }}
                    className="flex items-end gap-3"
                  >
                    <div className="flex-1">
                      <FormField
                        id="matchTerm"
                        label="Search courses by name"
                        value={matchTerm}
                        onChange={(e) => setMatchTerm(e.target.value)}
                      />
                    </div>
                    {/* mb-6 matches FormField's own bottom margin, so the button lines up with
                        the input rather than with the margin under it. */}
                    <Button
                      type="submit"
                      variant="secondary"
                      isLoading={isSearching}
                      className="mb-6"
                    >
                      Search
                    </Button>
                  </form>

                  {!isSearching && matches.length === 0 && (
                    <p className="mt-3 text-sm text-gray-500">
                      No courses match that name.
                    </p>
                  )}

                  {matches.length > 0 && (
                    <ul className="mt-3 divide-y divide-gray-100 rounded-lg border border-gray-200">
                      {matches.map((course) => (
                        <li
                          key={course.id}
                          className="flex items-center justify-between gap-4 px-4 py-3"
                        >
                          <div className="min-w-0">
                            <p className="truncate text-sm font-medium text-ink">
                              {course.name}
                            </p>
                            <p className="truncate text-xs text-gray-500">
                              {course.address}
                            </p>
                          </div>
                          <Button
                            variant="secondary"
                            onClick={() => handleLink(course)}
                            disabled={isBusy}
                          >
                            Link
                          </Button>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>

                <div className="border-t border-gray-100 pt-4">
                  <Button
                    variant="danger"
                    onClick={handleDecline}
                    disabled={isBusy}
                  >
                    Decline request
                  </Button>
                </div>
              </div>
            </Card>
          )}
        </div>
      )}
    </AdminLayout>
  );
};

export default CourseRequestReview;
